<template>
    <Dialog
        :value="dialogVisible"
        :header-text="$t('Import finished!')"
        :width="500"
        :actionsRightAlign="true"
        :className="'light-gradient'"
        :textColor="'white--text'"
        :showLogo="true"
        persistent
        hide-close-icon
        fixed
        @close_modal="$emit('close')"
    >
        <div
            v-if="lastExcelImportData && lastExcelImportData.file_type === 'xlsx' && lastExcelImportData.failed_count > 0"
            class="error lighten-2 rounded-lg pa-4 d-flex align-center"
        >
            <div class="flex-shrink-0">
                <icon
                    icon="AlertTriangle"
                    size="32"
                    color="error"
                />
            </div>
            <div class="font-weight-medium mx-2">
                {{ $t('Failed to import {0} records').format(lastExcelImportData.failed_count) }}
            </div>
            <v-spacer />
            <a :href="`/api/import/${lastExcelImportData.import_session_id}/skipped`">
                <primary-button
                    color="main"
                    class="white--text"
                >
                    {{ $t("Download report") }}
                </primary-button>
            </a>
        </div>
        <div v-if="lastExcelImportData" class="body-2">
            <br>
            <div>
                <template v-if="lastExcelImportData.failed_count === 1 && lastExcelImportData.file_type === 'xlsx'">
                    {{ $t('Result: added {0}, updated {1}, skipped {2}, failed to import {3}').format(lastExcelImportData.imported_count, lastExcelImportData.updated_count, lastExcelImportData.skipped_count, lastExcelImportData.failed_count) }}
                </template>
                <template v-else-if="lastExcelImportData.failed_count && lastExcelImportData.file_type === 'xlsx'">
                    {{ $t('Result: added {0}, updated {1}, skipped {2}, failed to import {3}').format(lastExcelImportData.imported_count, lastExcelImportData.updated_count, lastExcelImportData.skipped_count, lastExcelImportData.failed_count) }}
                </template>
                <template v-else>
                    {{ $t('Result: added {0}, updated {1}, skipped {2}').format(lastExcelImportData.imported_count, lastExcelImportData.updated_count, lastExcelImportData.skipped_count) }}
                </template>
            </div>
        </div>
        <template v-if="hasAnySuccessfulRows">
            <div class="title pt-5">
                {{ $t('What’s next?') }}
            </div>
            <div class="body-2 mb-3">
                {{ $t('To work effectively with your client base, complete the following steps:') }}
            </div>

            <v-row no-gutters class="pl-1">
                <v-col>
                    <template v-for="(bullet, index) in bullets">
                        <v-row
                            :key="index"
                            no-gutters
                        >
                            <v-col>
                                <span class="mr-2">{{ index + 1 }}.</span>
                                <span
                                    class="mr-1"
                                    v-html="bullet.text"
                                /> <!-- INPUT FROM STATIC TRANSLATION -->
                                <a
                                    target="_blank"
                                    :href="bullet.link"
                                >{{ $t("Read more ›") }}
                                </a>
                            </v-col>
                        </v-row>
                    </template>
                </v-col>
            </v-row>

            <v-row
                class="mt-3"
            >
                <v-col class="pb-0">
                    {{ $t("Next you will only see active imported clients. To see all clients, remove “Last import” filter.") }}
                </v-col>
            </v-row>
        </template>
        <template slot="footer">
            <router-link :to="viewClientsLink">
                <primary-button @click="dialogVisible = false">
                    {{ $t("View clients") }}
                </primary-button>
            </router-link>
        </template>
    </Dialog>
</template>

<script>
import {computed, ref, watch} from "@vue/composition-api";
import { $t } from "@/i18n";
import Dialog from "@/components/Dialog";
import { getRegionAppTitle } from "@/helpers/helpers";
import { APP_STORE_APP_TYPES } from "@/helpers/constants";
import AccountModule from "@/store/modules/account";
import NotificationModule from "@/store/modules/notification"
import { HELP_LINKS } from "@/helpers/constants";
import HelpModule from "@/modules/help/help"
import {parseSysDateTime} from "@/plugins/formatting";

export default {
    name: "ImportDoneModal",
    components: {
        Dialog
    },
    props: {
        overrideLastExcelImportData: {
            type: Object,
            default: null
        },
        overrideCompanyId: {
            type: Number,
            default: null
        }
    },
    setup(props) {
        const { getAccount, current_account } = AccountModule();
        const companyId = ref(props.overrideCompanyId ? props.overrideCompanyId : null);
        const selectedAccount = computed(() => companyId ? getAccount(props.companyId).value : null)
        const dialogVisible = ref(!!props.overrideLastExcelImportData);
        const notificationModule = NotificationModule()
        const lastNotifiedTime = ref(null);
        const lastExcelImportData = ref(props.overrideLastExcelImportData ? props.overrideLastExcelImportData : null);

        const getTimezoneOffset = (timeZone, date = new Date()) => {
            const tz = date.toLocaleString("en", {timeZone, timeStyle: "long"}).split(" ").slice(-1)[0];
            const dateString = date.toString();
            const offset = Date.parse(`${dateString} UTC`) - Date.parse(`${dateString} ${tz}`);

            // return UTC offset in millis
            return offset;
        }

        let browserTimezoneOffsetMillis = getTimezoneOffset(Intl.DateTimeFormat().resolvedOptions().timeZone);
        let userSettingTimezoneOffsetMillis = getTimezoneOffset(current_account.value.timezone)

        // Notification created at is returned with timezone offset applied, currently we remove the timezone offset that the browser uses and then add timezone offset that the user has as their settings
        // Should maybe make it so notification created_at is utc
        lastNotifiedTime.value = new Date(Date.now() - browserTimezoneOffsetMillis + userSettingTimezoneOffsetMillis)

        const bullets = computed(() => {
            let data = [
                {
                    text: $t("Add <b>primary member</b> to every client."),
                    link: HelpModule.getHelpLink(HELP_LINKS.MERIT_SUCCESS_PRIMARY_USER)
                },
                {
                    text: $t("Add <b>task plan</b> to automate recurring tasks."),
                    link: HelpModule.getHelpLink(HELP_LINKS.MERIT_SUCCESS_TASK_PLAN)
                }
            ]

            if (lastExcelImportData && lastExcelImportData.file_type === 'merit_aktiva') {
                data.push({
                    text: $t("Setup automatic monthly summary under {0} app in Uku to import volume of work.")
                        .format(getRegionAppTitle(APP_STORE_APP_TYPES.MERIT_AKTIVA, selectedAccount.value ? selectedAccount.value.region_code : null)),
                    link: HelpModule.getHelpLink(HELP_LINKS.MERIT_SUCCESS_AUTOMATIC_MONTHLY_SUMMARY)
                })
            }

            return data
        });

        watch(notificationModule.list, async (newValues, oldValues) => {
            if (props.lastExcelImportData) {
                return
            }

            if (!newValues || !oldValues) {
                return
            }

            const changedItems = newValues.filter(x => !oldValues.find(z => z.id === x.id))
            const notification = changedItems.find(x => {
                if (x.activity.type !== 'import_finished') {
                    return false
                }

                if (x.is_alerted) {
                    return false
                }

                if (parseSysDateTime(x.created_at) < lastNotifiedTime.value) {
                    return false
                }

                return true
            })

            if (notification) {
                lastExcelImportData.value = notification.data
                dialogVisible.value = true
                companyId.value = notification.activity.account_id
                lastNotifiedTime.value = parseSysDateTime(notification.created_at)
            }
        })

        const hasAnySuccessfulRows = computed(() => {
            return lastExcelImportData.value && (lastExcelImportData.value.imported_count || lastExcelImportData.value.updated_count)
        })

        const viewClientsLink = computed(() => {
            let link = {
                name: 'clients_list',
                query: {
                    account_id: companyId.value,
                    client_last_import: 1
                }
            }

            return link
        })

        return {
            bullets,
            selectedAccount,
            dialogVisible,
            companyId,
            notificationModule,
            lastExcelImportData,
            hasAnySuccessfulRows,
            viewClientsLink
        };
    }
};
</script>
